import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import type { ReactNode } from 'react';
import { type DateWindow, usePanelValues } from '../../../lib/filters.hooks';
import type { ScopeOption, ScopeState } from '../../filters/types';
import { useScopeOptions } from '../../filters/useScopeOptions';
import { MEDIA_PANEL_KEYS } from '../data/constants';
import type { MediaFilters, MediaKpi } from '../data/types';
import { marketName } from '../data/utils';

interface ReportScopeOption {
  id: string;
  label: string;
}

interface ReportScopeInput {
  selections: {
    products: string[];
    networks: string[];
    accounts: string[];
    markets: string[];
    types: string[];
    objectives: string[];
    kpi: string[];
  };
  options: {
    products: ReportScopeOption[];
    networks: ReportScopeOption[];
    accounts: ReportScopeOption[];
    markets: ReportScopeOption[];
    types: ReportScopeOption[];
    objectives: ReportScopeOption[];
    kpi: ReportScopeOption[];
  };
  dateWindow: [string, string];
}

interface ReportScopeItem {
  label: string;
  value: string;
}

export function buildMediaReportScopeItems(_input: ReportScopeInput): ReportScopeItem[] {
  const { selections, options, dateWindow } = _input;
  const selectedValue = (ids: string[], values: ReportScopeOption[], allLabel: string): string =>
    ids.length
      ? ids.map((id) => values.find((option) => option.id === id)?.label ?? id).join(', ')
      : allLabel;
  const dateFormatter = new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });
  const formatDate = (value: string): string =>
    dateFormatter.format(new Date(`${value}T00:00:00Z`));

  return [
    {
      label: 'Products',
      value: selectedValue(selections.products, options.products, 'All products'),
    },
    {
      label: 'Networks',
      value: selectedValue(selections.networks, options.networks, 'All networks'),
    },
    {
      label: 'Accounts',
      value: selectedValue(selections.accounts, options.accounts, 'All accounts'),
    },
    {
      label: 'Markets',
      value: selectedValue(selections.markets, options.markets, 'All markets'),
    },
    { label: 'Types', value: selectedValue(selections.types, options.types, 'All types') },
    {
      label: 'Objectives',
      value: selectedValue(selections.objectives, options.objectives, 'All objectives'),
    },
    { label: 'KPI', value: selectedValue(selections.kpi, options.kpi, '-') },
    {
      label: 'Date range',
      value: `${formatDate(dateWindow[0])} – ${formatDate(dateWindow[1])}`,
    },
  ];
}

interface PanelBucket {
  values: string[];
  labels?: Record<string, string>;
}

function toOptions(
  bucket: PanelBucket | undefined,
  rename?: (value: string) => string,
): ScopeOption[] {
  return (bucket?.values ?? []).map((value) => ({
    id: value,
    label: rename ? rename(value) : (bucket?.labels?.[value] ?? value),
  }));
}

export function MediaReportScope({
  scope,
  filters,
  dateWindow,
  accountIds,
  kpiOptions,
}: {
  scope: ScopeState;
  filters: MediaFilters;
  dateWindow: DateWindow;
  accountIds: number[];
  kpiOptions: MediaKpi[];
}): ReactNode {
  const { brandOptions, platformOptions, accountOptions } = useScopeOptions(scope);
  const { data: panelValues } = usePanelValues(MEDIA_PANEL_KEYS, undefined, dateWindow, accountIds);
  const items = buildMediaReportScopeItems({
    selections: {
      products: scope.brand,
      networks: scope.platform,
      accounts: scope.account,
      markets: filters.countries,
      types: filters.creativeTypes,
      objectives: filters.objectives,
      kpi: [filters.kpi],
    },
    options: {
      products: brandOptions,
      networks: platformOptions,
      accounts: accountOptions,
      markets: toOptions(panelValues?.country, marketName),
      types: toOptions(panelValues?.creativeType),
      objectives: toOptions(panelValues?.campaignObjective),
      kpi: kpiOptions,
    },
    dateWindow,
  });

  return (
    <Box
      sx={(theme) => ({
        display: 'none',
        '@media print': {
          display: 'flex',
          alignItems: 'baseline',
          flexWrap: 'wrap',
          gap: 1,
          mb: 2,
          py: 1,
          px: 2,
          border: '1px solid',
          borderColor: theme.palette.border.default,
          borderRadius: `${theme.radius.xl}px`,
          breakInside: 'avoid',
        },
      })}
    >
      <Typography sx={{ fontSize: 10, fontWeight: 700, color: 'text.primary', mr: 1 }}>
        Report scope:
      </Typography>
      <Box
        sx={{
          display: 'flex',
          flex: 1,
          minWidth: 0,
          flexWrap: 'wrap',
          columnGap: 2,
          rowGap: 1,
        }}
      >
        {items.map((item, index) => (
          <Typography
            key={item.label}
            component="span"
            sx={{ fontSize: 9, color: 'text.secondary', overflowWrap: 'anywhere' }}
          >
            <Typography
              component="span"
              variant="inherit"
              sx={{ fontWeight: 600, color: 'text.disabled' }}
            >
              {`${item.label}: `}
            </Typography>
            <Typography
              component="span"
              variant="inherit"
              sx={{ fontWeight: 600, color: 'text.primary' }}
            >
              {item.value}
            </Typography>
            {index < items.length - 1 ? ' ·' : ''}
          </Typography>
        ))}
      </Box>
    </Box>
  );
}
